export type ProfileListingSortRow = {
  id: string;
  quantity?: number | null;
  created_at: string;
  last_sold_at?: string | null;
};

export function isProfileListingInStock(quantity: number | null | undefined): boolean {
  return (quantity ?? 0) > 0;
}

export function profileListingSortTimestamp(
  listing: ProfileListingSortRow,
  lastSoldAtByListingId?: ReadonlyMap<string, string> | null,
): number {
  const inStock = isProfileListingInStock(listing.quantity);
  const createdAt = new Date(listing.created_at).getTime();
  if (inStock) {
    return createdAt;
  }
  const soldAtRaw = listing.last_sold_at ?? lastSoldAtByListingId?.get(listing.id);
  if (soldAtRaw) {
    return new Date(soldAtRaw).getTime();
  }
  return createdAt;
}

export function compareProfileListings(
  a: ProfileListingSortRow,
  b: ProfileListingSortRow,
  lastSoldAtByListingId?: ReadonlyMap<string, string> | null,
): number {
  const aInStock = isProfileListingInStock(a.quantity);
  const bInStock = isProfileListingInStock(b.quantity);
  if (aInStock !== bInStock) {
    return aInStock ? -1 : 1;
  }
  return profileListingSortTimestamp(b, lastSoldAtByListingId) - profileListingSortTimestamp(a, lastSoldAtByListingId);
}

export function sortProfileListings<T extends ProfileListingSortRow>(
  listings: T[],
  lastSoldAtByListingId?: ReadonlyMap<string, string> | null,
): T[] {
  return [...listings].sort((a, b) => compareProfileListings(a, b, lastSoldAtByListingId));
}

export async function buildLastSoldAtByListingId(
  supabase: { from: (table: string) => any },
  listingIds: string[],
): Promise<Map<string, string>> {
  const map = new Map<string, string>();
  if (listingIds.length === 0) {
    return map;
  }

  const chunkSize = 200;
  for (let i = 0; i < listingIds.length; i += chunkSize) {
    const chunk = listingIds.slice(i, i + chunkSize);
    const { data, error } = await supabase
      .from("order_items")
      .select("listing_id, created_at")
      .in("listing_id", chunk);
    if (error) {
      throw error;
    }
    for (const row of data ?? []) {
      const listingId = row.listing_id as string | null;
      const createdAt = row.created_at as string | null;
      if (!listingId || !createdAt) continue;
      const existing = map.get(listingId);
      if (!existing || createdAt > existing) {
        map.set(listingId, createdAt);
      }
    }
  }

  return map;
}
